
import React, { useEffect, Suspense } from 'react';
import { BrowserRouter, Route, Routes, useParams, useNavigate } from "react-router-dom";
import Contact from './components/Contact/Contact';
import Header from './components/common/Header/Header';
import ScrollToTop from './components/common/ScrollToTop';
import ErrorBoundary from './components/common/ErrorBoundary';
import '../src/styles/global.css';
import '../src/styles/seasonal.css';
import '../src/styles/theme-variables.css';
import { SeasonalThemeProvider } from './contexts/SeasonalThemeProvider';
import { LanguageProvider } from './contexts/LanguageProvider';
import { ThemeProvider } from './contexts/ThemeProvider';
import { initMemoryMonitoring } from './utils/memoryUtils';
import { allValidRoutesInfo, allRouteSlugs, serviceRoutes, insuranceRoutes, financingRoutes } from './config/routes';
import type { RouteInfo } from './config/routes';
import NotFoundPage from './pages/NotFound';
import { useSeasonalTheme } from './hooks/useSeasonalTheme';
import Home from './pages/Home';

const LazyServicePage = React.lazy(() =>
    Promise.all([
        import('./components/service/ServicePage'),
        import('./content/services'),
    ]).then(([mod, registry]) => ({
        default: (props: { contentKey: string }) => {
            const Component = mod.default;
            return <Component content={registry.default[props.contentKey]} />;
        },
    }))
);

const LazyInsurancePage = React.lazy(() =>
    Promise.all([
        import('./components/insurance/InsurancePage'),
        import('./content/insurance'),
    ]).then(([mod, registry]) => ({
        default: (props: { contentKey: string }) => {
            const Component = mod.default;
            return <Component content={registry.default[props.contentKey]} />;
        },
    }))
);

const LazyFinancingPage = React.lazy(() =>
    Promise.all([
        import('./components/financing/FinancingPage'),
        import('./content/financing'),
    ]).then(([mod, registry]) => ({
        default: (props: { contentKey: string }) => {
            const Component = mod.default;
            return <Component content={registry.default[props.contentKey]} />;
        },
    }))
);

// ★ Route-level lazy loading — each page is only loaded when navigated to
const OurServices = React.lazy(() => import("./pages/OurServices"));
const AboutUs = React.lazy(() => import("./pages/about/AboutUs"));
const ContactUs = React.lazy(() => import("./pages/ContactUs"));
const PatientPortal = React.lazy(() => import('./pages/PatientPortal'));
const BookAppointment = React.lazy(() => import('./pages/booking/BookAppointment'));
const ConsentFormSigning = React.lazy(() => import("./pages/consent/ConsentFormSigning"));

// Insurance pages (only non-migrated ones remain as lazy imports)
const Insurance = React.lazy(() => import('./pages/insurance/Insurance'));
const Medicare = React.lazy(() => import('./pages/insurance/Medicare'));

// Financing pages (only non-migrated ones remain as lazy imports)
const PaymentOptions = React.lazy(() => import("./pages/financing/PaymentOptions"));
const InHousePaymentPlan = React.lazy(() => import("./pages/financing/InHousePaymentPlan"));
const Financing = React.lazy(() => import('./pages/financing/Financing'));

// Legal pages
const PrivacyPolicy = React.lazy(() => import("./pages/legal/PrivacyPolicy"));
const TermsOfService = React.lazy(() => import("./pages/legal/TermsOfService"));

// Careers pages
const JoinOurTeam = React.lazy(() => import("./pages/careers/JoinOurTeam"));

// Blog pages
const Blog = React.lazy(() => import("./pages/blog/Blog"));
const BlogDetail = React.lazy(() => import("./pages/blog/BlogDetail"));

const PayYourBill = React.lazy(() => import("./pages/PayYourBill"));



// NotFoundPage is now in src/pages/NotFound.tsx

// ===========================================================================
// DynamicRedirectHandler — fuzzy-matches partial slugs to known routes
// ===========================================================================
interface DynamicRedirectHandlerProps {
  allRoutesInfo: RouteInfo[];
}

const DynamicRedirectHandler: React.FC<DynamicRedirectHandlerProps> = ({ allRoutesInfo }) => {
  const { partialSlug: rawPartialSlug } = useParams<{ partialSlug?: string }>();
  const navigate = useNavigate();

  useEffect(() => {
    if (rawPartialSlug) {
      const normalizedPartialSlug = rawPartialSlug.toLowerCase().replace(/\s+/g, '-');

      let matchedRoute: RouteInfo | undefined = undefined;

      // 1. Exact match
      matchedRoute = allRoutesInfo.find((route: RouteInfo) => route.slug === normalizedPartialSlug);

      // 2. StartsWith match
      if (!matchedRoute) {
        matchedRoute = allRoutesInfo.find((route: RouteInfo) => route.slug.startsWith(normalizedPartialSlug));
      }

      // 3. Includes match
      if (!matchedRoute) {
        matchedRoute = allRoutesInfo.find((route: RouteInfo) => route.slug.includes(normalizedPartialSlug));
      }

      if (matchedRoute) {
        console.log(`Redirecting from /${rawPartialSlug} to ${matchedRoute.path}`);
        navigate(matchedRoute.path, { replace: true });
      } else {
        console.log(`No suitable route found for /${rawPartialSlug}. Showing 404.`);
        navigate('/404', { replace: true });
      }
    }
  }, [rawPartialSlug, navigate, allRoutesInfo]);

  return null;
};

// ===========================================================================
// AppContent — inner component that can read the SeasonalThemeProvider context
// ===========================================================================
const AppContent: React.FC = () => {
  const { season } = useSeasonalTheme();

  // Build a CSS class for the season (e.g. "season-christmas", "season-halloween")
  const seasonClass = season !== 'default' ? `season-${season}` : '';

  return (
    <>
      <ScrollToTop />
      <Header routes={allRouteSlugs} />
      <div className={`page-content ${seasonClass}`.trim()} data-season={season}>
        <ErrorBoundary>
          <Suspense fallback={null}>
            <Routes>
              {/* ── Non-service pages (unique layouts) ─────────────── */}
              <Route path="/" element={<Home />} />
              <Route path="about-us/" element={<AboutUs />} />
              <Route path="contact/" element={<Contact />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogDetail />} />
              <Route path="services/" element={<OurServices />} />
              <Route path="payment-options/" element={<PaymentOptions />} />
              <Route path="financing-options/" element={<Financing />} />
              <Route path="private-insurance/" element={<Insurance />} />
              <Route path="in-house-payment-plan/" element={<InHousePaymentPlan />} />
              <Route path="join-our-team/" element={<JoinOurTeam />} />
              <Route path="privacy-policy/" element={<PrivacyPolicy />} />
              <Route path="terms-of-service/" element={<TermsOfService />} />
              <Route path="medicare/" element={<Medicare />} />
              <Route path="contact-us/" element={<ContactUs />} />
              <Route path="/insurance" element={<Insurance />} />
              <Route path="/financing" element={<Financing />} />
              <Route path="/consent-form/:token" element={<ConsentFormSigning />} />
              <Route path="/consent-form/:token/" element={<ConsentFormSigning />} />
              <Route path="/patient-portal" element={<PatientPortal />} />
              <Route path="/pay-your-bill" element={<PayYourBill />} />
              <Route path="/book-appointment" element={<BookAppointment />} />
              {/* ── Service pages (lazy-loaded with content registry) ── */}
              {serviceRoutes.map(route => (
                <Route
                  key={route.path}
                  path={route.path}
                  element={<LazyServicePage contentKey={route.contentKey!} />}
                />
              ))}

              {/* ── Insurance pages (lazy-loaded with content registry) ── */}
              {insuranceRoutes.map(route => (
                <Route
                  key={route.path}
                  path={route.path}
                  element={<LazyInsurancePage contentKey={route.insuranceContentKey!} />}
                />
              ))}

              {/* ── Financing pages (lazy-loaded with content registry) ── */}
              {financingRoutes.map(route => (
                <Route
                  key={route.path}
                  path={route.path}
                  element={<LazyFinancingPage contentKey={route.financingContentKey!} />}
                />
              ))}

              {/* ── Dynamic redirect (fuzzy match partial slugs) ──── */}
              <Route path=":partialSlug" element={<DynamicRedirectHandler allRoutesInfo={allValidRoutesInfo} />} />
              <Route path=":partialSlug/" element={<DynamicRedirectHandler allRoutesInfo={allValidRoutesInfo} />} />

              {/* ── 404 catch-all ──────────────────────────────────── */}
              <Route path="/404" element={<NotFoundPage />} />
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Suspense>
        </ErrorBoundary>
      </div>
    </>
  );
};

// ===========================================================================
// Main App Component
// ===========================================================================
function App() {
  useEffect(() => {
    initMemoryMonitoring();
  }, []);

  return (
    <BrowserRouter>
      <ThemeProvider>
        <LanguageProvider>
          <SeasonalThemeProvider>
            <AppContent />
          </SeasonalThemeProvider>
        </LanguageProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
